/**
 * TasksTierGuard — Deep-Link-Schutz für die beiden Aufgaben-Zusatzstufen
 * (Plan-Freigabe 08/2026).
 *
 * Das Aufgaben-Feature ist dreistufig auf die Tarife verteilt:
 * Basis (`feature.tasks`, 64) ab Starter, Routinen/Vorlagen
 * (`feature.tasks_routines`, 66) ab Plus, Compliance/Nachweis
 * (`feature.tasks_compliance`, 67) nur Pro.
 *
 * Der Guard liest `route.data.tierFeature` und prüft die Stufe über den
 * {@link SubscriptionService} — dasselbe Muster wie {@link TasksFeatureGuard}, inklusive
 * dessen **fail-open**-Regel: ist der Abo-Stand unbekannt (Backend beim ersten Login kurz
 * nicht erreichbar), wird NICHT gesperrt. Ein leeres Feature-Set als „nicht gebucht" zu
 * lesen, schickte Nutzer auf die Abo-Seite, obwohl der Tarif die Stufe enthält. Das
 * Backend gated ohnehin serverseitig (403 `TASK_ROUTINES_NOT_IN_PLAN` /
 * `TASK_COMPLIANCE_NOT_IN_PLAN`).
 *
 * **Abgrenzung zum {@link TaskSurfaceGuard}:** der prüft *Rechte* und leitet auf
 * `/tasks/permission-denied`. Hier geht es um den *Tarif* — Ziel ist deshalb die
 * Abo-Seite (bzw. `/tasks` auf iOS, wo Kaufhinweise nicht erlaubt sind). Beide Guards
 * hängen an denselben Routen und sind unabhängig voneinander: „darf nicht" und
 * „nicht gebucht" sind zwei verschiedene Antworten.
 */
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { SubscriptionService } from '../../feature-toggle/services/subscription.service';
import { Feature } from '../../feature-toggle/enums/features.enum';
import { PlatformService } from 'src/app/core/services/platform/platform.service';

/** Erlaubte Werte für `route.data.tierFeature`. */
export type TasksTier = 'routines' | 'compliance';

const TIER_FEATURE: Record<TasksTier, Feature> = {
  routines: Feature.TASK_ROUTINES,
  compliance: Feature.TASK_COMPLIANCE,
};

@Injectable({ providedIn: 'root' })
export class TasksTierGuard implements CanActivate {
  constructor(
    private readonly subscriptionService: SubscriptionService,
    private readonly router: Router,
    private readonly platformService: PlatformService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const tier = route.data['tierFeature'] as TasksTier | undefined;
    if (!tier) {
      // Ohne konfigurierte Stufe hat der Guard nichts zu prüfen. Bewusst durchlassen
      // statt sperren: eine vergessene `data`-Angabe darf keine Route unerreichbar machen.
      return of(true);
    }

    return this.subscriptionService.ensureFeaturesLoaded().pipe(
      map((featuresLoaded) => {
        if (!featuresLoaded) return true; // Stand unbekannt → fail-open
        if (this.subscriptionService.hasFeature(TIER_FEATURE[tier])) return true;
        return this.router.createUrlTree([
          this.platformService.isIos() ? '/tasks' : '/settings/subscription',
        ]);
      }),
    );
  }
}
